import React, { useState } from 'react';
import { getIncompleteStateNew, Spacer } from 'src/common';
import { LogSwitch } from 'src/components/charts/comps';
import ExpandSection from 'src/components/ExpandSection';
import styled from 'styled-components';
import useBreakpoints from 'src/hooks/useBreakpoints';
import ChartBlank from 'src/components/charts/ChartBlank';
import { LegendComposed } from './comps/LegendComposed';
import { ScenarioQuery, VersionsQuery } from 'src/graphql/_generated';
import { plotLineGradient } from 'src/components/charts/util';
import { useStateSelector } from 'src/redux';
import { ChartConf } from 'src/components/charts/Chart/Chart';
import Chart from 'src/components/charts/Chart';

const Div = styled.div`
  display: flex;
  align-items: center;
  grid-gap: 20px;

  ${({ theme }) => theme.breakpoints.down('md')} {
    grid-gap: 10px;
  }
`;

interface ExceedanceSectionProps {
  isLogarithmic: boolean;
  setIsLogarithmic: (isLogarithmic: boolean) => void;
  showAllProjected: boolean;
  scenario: NonNullable<ScenarioQuery['scenario']>;
  version: NonNullable<VersionsQuery['assessment']>['versions'][number] | undefined;
}

export const ExceedanceSection: React.FC<ExceedanceSectionProps> = ({
  isLogarithmic,
  showAllProjected,
  setIsLogarithmic,
  scenario,
  version,
}) => {
  const colors = useStateSelector(({ theme }) => theme.colors);
  const [isOpen, setIsOpen] = useState(true);
  const isMd = useBreakpoints('md');

  const input = (
    !version?.legacyVersion
      ? [
          showAllProjected
            ? {
                data: scenario?.baseRisk?.chart || [],
                upper: scenario?.baseRisk?.upper || 1,
                lower: scenario?.baseRisk?.lower || 1,
                name: 'Unmodified Risk',
                color: colors.prussianBlue,
                fillColor: 'rgba(2, 3, 51, 0.07)',
                legendId: 'scenario_leg-base-m',
              }
            : null,
          showAllProjected
            ? {
                data: scenario?.projectedRisk?.chart || [],
                upper: scenario?.projectedRisk?.upper || 1,
                lower: scenario?.projectedRisk?.lower || 1,
                name: 'Projected Risk',
                color: colors.brightBlue,
                fillColor: 'rgba(100, 149, 237, 0.07)',
                legendId: 'scenario_leg-s2-m',
              }
            : null,
          {
            data: scenario?.liveRisk?.chart || [],
            upper: scenario?.liveRisk?.upper || 1,
            lower: scenario?.liveRisk?.lower || 1,
            name: 'Live Risk',
            color: plotLineGradient,
            fillColor: 'rgba(230, 46, 97, 0.07)',
            legendId: 'scenario_leg-s1-md',
          },
        ].filter(Boolean)
      : [
          {
            data: scenario?.baseRisk?.chart || [],
            upper: scenario?.baseRisk?.upper || 1,
            lower: scenario?.baseRisk?.lower || 1,
            name: 'Projected Risk',
            color: plotLineGradient,
            fillColor: colors.aliceBlue,
            legendId: 'legend1',
          },
        ]
  ) as ChartConf[];

  const canRenderChart = !(scenario.liveRisk?.expectedLoss === null || getIncompleteStateNew(scenario) === 'TECH');

  return (
    <ExpandSection
      title="LOSS EXCEEDANCE CURVE"
      sectionId="scenario_exceedance"
      onChange={setIsOpen}
      endComponent={
        <Div style={{ display: isOpen ? 'flex' : 'none' }}>
          {!version?.legacyVersion && canRenderChart && !isMd && <LegendComposed showAllProjected={showAllProjected} />}
          {scenario.liveRisk.expectedLoss != null && (
            <LogSwitch skey="log:scenario" isLogarithmic={isLogarithmic} setIsLogarithmic={setIsLogarithmic} />
          )}
        </Div>
      }
    >
      {!version?.legacyVersion && canRenderChart && isMd && (
        <div style={{ display: 'flex' }}>
          <LegendComposed showAllProjected={showAllProjected} />
        </div>
      )}
      <Spacer $px={20} />
      {!canRenderChart ? <ChartBlank techIncomplete /> : <Chart isLogarithmic={isLogarithmic} input={input} />}
    </ExpandSection>
  );
};
